import React, { useEffect, useState } from 'react';
import supabase from '../supabase/supabase';
import { FiCheck, FiX, FiHash } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

export default function AdminApprovals({ refreshKey, onApproveComplete }) {
  const [uploads, setUploads] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  // States for inline editing
  const [editData, setEditData] = useState({});

  useEffect(() => {
    fetchData();
  }, [refreshKey]);

  const fetchData = async () => {
    const [uploadsRes, catsRes] = await Promise.all([
      supabase.from('pending_uploads_1715000000').select('*, categories_1715000000(name)').eq('status', 'pending').order('created_at', { ascending: false }),
      supabase.from('categories_1715000000').select('*').order('name')
    ]);
    
    if (uploadsRes.data) {
      setUploads(uploadsRes.data);
      // Initialize edit data state
      const initialEdits = {};
      uploadsRes.data.forEach(u => {
        initialEdits[u.id] = {
          categoryId: u.category_id || '',
          tags: ''
        };
      });
      setEditData(initialEdits);
    }
    if (catsRes.data) setCategories(catsRes.data);
    setLoading(false);
  };

  const handleEditChange = (id, field, value) => {
    setEditData(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value }
    }));
  };

  const handleGenerateAITags = async (upload) => {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    if (!apiKey) return alert("OpenAI API key missing!");
    
    setProcessingId(upload.id);
    try {
      const catName = categories.find(c => c.id === editData[upload.id].categoryId)?.name || upload.custom_category || "general";
      const promptText = `Analyze this image and write exactly 3 relevant hashtags (without the # symbol). Since this image is categorized under "${catName}", ensure the hashtags are highly relevant to the niche. Return exactly this JSON format: {"tags": ["tag1", "tag2", "tag3"]}`;
      
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          response_format: { type: "json_object" },
          messages: [
            { role: 'user', content: [{ type: 'text', text: promptText }, { type: 'image_url', image_url: { url: upload.image_url } }] }
          ],
          max_tokens: 300
        })
      });

      const data = await response.json();
      if (data.error) throw new Error(data.error.message);
      const aiResult = JSON.parse(data.choices[0].message.content);
      
      handleEditChange(upload.id, 'tags', aiResult.tags.join(', '));
    } catch (err) {
      alert(`Failed: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleApprove = async (upload) => {
    setProcessingId(upload.id);
    try {
      const finalCategory = editData[upload.id].categoryId || null;
      const tagsArray = editData[upload.id].tags.split(',').map(t => t.trim()).filter(Boolean);

      // Insert to posts
      const { error: insertError } = await supabase.from('posts_1715000000').insert([{
        title: "Community Upload",
        image_url: upload.image_url,
        category_id: finalCategory,
        tags: tagsArray
      }]);
      if (insertError) throw insertError;

      // Update status
      const { error: updateError } = await supabase.from('pending_uploads_1715000000').update({ status: 'approved' }).eq('id', upload.id);
      if (updateError) throw updateError;

      fetchData();
      if (onApproveComplete) onApproveComplete();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (id) => {
    if (!confirm('Reject this upload?')) return;
    setProcessingId(id);
    try {
      await supabase.from('pending_uploads_1715000000').update({ status: 'rejected' }).eq('id', id);
      fetchData();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) return <div className="font-bold text-retro-brown bg-white p-8 rounded-3xl border-4 border-retro-brown shadow-retro">Loading approvals...</div>;

  return (
    <div className="bg-white p-8 rounded-3xl shadow-retro border-4 border-retro-brown overflow-hidden">
      <h2 className="font-display text-2xl tracking-wider text-retro-brown uppercase mb-6 drop-shadow-[1px_1px_0_#13A09E]">Upload Approvals</h2>
      
      <div className="space-y-6">
        {uploads.map((upload) => (
          <div key={upload.id} className="bg-retro-cream border-4 border-retro-brown rounded-2xl p-6 shadow-retro-sm flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/3 max-w-[250px] flex-shrink-0">
              <div className="border-4 border-retro-brown rounded-xl overflow-hidden shadow-retro-sm bg-gray-100">
                <img src={upload.image_url} alt="Upload Request" className="w-full h-auto object-cover" />
              </div>
            </div>

            <div className="flex-1 flex flex-col gap-4">
              <div className="bg-white border-2 border-retro-brown p-4 rounded-xl shadow-retro-sm">
                <p className="font-bold text-retro-brown/60 text-sm mb-1">Submitted by</p>
                <p className="font-bold text-retro-brown text-lg">{upload.email}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className="bg-retro-yellow text-retro-brown border-2 border-retro-brown px-3 py-1 rounded-full font-bold text-sm shadow-retro-sm uppercase">
                    Type: {upload.upload_type}
                  </span>
                  {upload.custom_category && (
                    <span className="bg-retro-lime text-retro-brown border-2 border-retro-brown px-3 py-1 rounded-full font-bold text-sm shadow-retro-sm uppercase">
                      Suggested Cat: {upload.custom_category}
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-bold text-retro-brown mb-2 text-sm">Assign Category</label>
                  <select 
                    value={editData[upload.id]?.categoryId || ''} 
                    onChange={(e) => handleEditChange(upload.id, 'categoryId', e.target.value)}
                    className="w-full p-3 border-2 border-retro-brown rounded-xl focus:outline-none focus:ring-4 focus:ring-retro-blue font-bold text-retro-brown bg-white shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] outline-none cursor-pointer"
                  >
                    <option value="">Uncategorized</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="flex items-center justify-between font-bold text-retro-brown mb-2 text-sm">
                    Assign Hashtags
                    <button 
                      onClick={() => handleGenerateAITags(upload)}
                      disabled={processingId === upload.id}
                      className="text-retro-blue hover:text-retro-teal underline text-xs flex items-center gap-1"
                    >
                      <SafeIcon icon={FiHash} /> Auto AI
                    </button>
                  </label>
                  <input 
                    type="text" 
                    placeholder="tag1, tag2, tag3"
                    value={editData[upload.id]?.tags || ''}
                    onChange={(e) => handleEditChange(upload.id, 'tags', e.target.value)}
                    className="w-full p-3 bg-white border-2 border-retro-brown rounded-xl focus:outline-none focus:ring-4 focus:ring-retro-blue font-bold text-retro-brown shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-auto pt-4 border-t-2 border-retro-brown/20">
                <button 
                  onClick={() => handleReject(upload.id)}
                  disabled={processingId === upload.id}
                  className="px-6 py-3 bg-white text-red-600 border-2 border-retro-brown rounded-xl font-bold shadow-retro-sm hover:shadow-retro hover:-translate-y-0.5 hover:bg-red-50 active:translate-y-1 active:shadow-retro-active transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  <SafeIcon icon={FiX} /> REJECT
                </button>
                <button 
                  onClick={() => handleApprove(upload)}
                  disabled={processingId === upload.id}
                  className="px-6 py-3 bg-retro-teal text-white border-2 border-retro-brown rounded-xl font-bold shadow-retro-sm hover:shadow-retro hover:-translate-y-0.5 active:translate-y-1 active:shadow-retro-active transition-all disabled:opacity-50 flex items-center gap-2 tracking-wide uppercase"
                >
                  <SafeIcon icon={FiCheck} /> APPROVE & PUBLISH
                </button>
              </div>
            </div>
          </div>
        ))}
        {uploads.length === 0 && (
          <div className="p-10 text-center font-bold text-xl text-retro-brown bg-retro-cream border-4 border-dashed border-retro-brown/30 rounded-2xl">
            No pending approvals.
          </div>
        )}
      </div>
    </div>
  );
}